import React, { useState, useCallback } from "react";
import { 
  SuccessMessage,
  FailMessage,
  Loading,
  CustomerSelect,
  CompanySelect,
  FormatDateToMDY,
  FormatValueToDecimal,
  DateSelect,
  PaymentMethodSelect
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from 'axios';
import { PDFDocument  } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export default function CollectionReport() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
//#endregion






//#region Customer 
  const [customer, setCustomer] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [company, setCompany] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);

  const openCustomerSelect = () => {
    setShowCustomerSelect(true)
  }
  const closeCustomerSelect = () => {
    setShowCustomerSelect(false)
  }
//#endregion







//#region Generate
  const fetchReportData = useCallback(async (_data) => {
    const body = {
      customerId: customer?.customerId || null,
      dateFrom: dateFrom,
      dateTo: dateTo,
      paymentMethodId: paymentMethod?.id || null
    }

    try {
      const response = await axios.post(`${apiUrl}/api/Report/report-data/collection-report`, body, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, [dateFrom, dateTo, customer, paymentMethod]);

  const generateHandleClick = async () => {
    if (!company || !dateFrom || !dateTo) {
      setFailMessage("Please fill up required fields");
      return;
    }

    setLoading(true);

    try {
      const _data = await fetchReportData(customer);

      if (!_data || _data.length === 0) {
        setFailMessage("No data found for the selected filter");
        setLoading(false);
        return;
      }

      // --- TEMPLATE LAYOUT COORDINATES ---
      const TABLE_LEFT = 35;
      const TABLE_RIGHT = 540;

      const COL_X_DATE = 35;
      const COL_X_DOC_NO = 110;
      const COL_X_CUSTOMER = 218;
      const COL_X_AMOUNT_RIGHT = 440;     // Right alignment for Amount
      const COL_X_UNAPPLIED_RIGHT = 550;  // Right alignment for Unapplied
      const PAGE_NO_X = 535;              // Position next to "Page:"

      const TABLE_START_Y = 574;          // Starting Y for table rows below header lines
      const ROW_HEIGHT = 13;              // Height between rows
      const BOTTOM_THRESHOLD = 80;        // Space to trigger new page

      // --- LOAD TEMPLATE AND FONTS ---
      const pdfUrl = `${window.location.origin}/docs/COLLECTION_REPORT.pdf`;
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();

      // Keep a clean copy of the template to duplicate pages if data exceeds Page 1
      const templateDoc = await PDFDocument.load(pdfBytes);
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontBytes = await (await fetch("/calibri.ttf")).arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);
      const boldBytes = await (await fetch("/calibrib.ttf")).arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      // Helper to right-align numeric text
      const drawTextRight = (targetPage, text, rightX, y, font, size) => {
        if (!text) return;
        const textStr = text.toString();
        const textWidth = font.widthOfTextAtSize(textStr, size);
        targetPage.drawText(textStr, {
          x: rightX - textWidth,
          y,
          size,
          font,
        });
      };

      // --- REUSABLE HEADER FUNCTION ---
      const drawHeader = (targetPage) => {
        // Retained Company details
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 732, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 722, size: 10, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 712, size: 10, font: calibriFont });

        // Template pre-printed label coordinates
        targetPage.drawText(`${FormatDateToMDY(dateFrom)} to ${FormatDateToMDY(dateTo)}`, { x: 105, y: 641, size: 10, font: calibriFont });
        targetPage.drawText(FormatDateToMDY(today || new Date()), { x: 105, y: 626, size: 10, font: calibriFont });
      };

      let currentPage = pdfDoc.getPages()[0];
      drawHeader(currentPage);

      let yPosition = TABLE_START_Y;
      let cashSalesTotal = 0;
      let cashCollectionsTotal = 0;

      // --- DRAW DATA ROWS ---
      for (let i = 0; i < _data.length; i++) {
        const item = _data[i];

        // Trigger new page if reaching the bottom
        if (yPosition < BOTTOM_THRESHOLD) {
          const [newPage] = await pdfDoc.copyPages(templateDoc, [0]);
          pdfDoc.addPage(newPage);
          currentPage = newPage;
          drawHeader(currentPage);
          yPosition = TABLE_START_Y;
        }

        const dateStr = item.date || item.Date ? FormatDateToMDY(item.date || item.Date) : "";
        const docNoStr = (item.documentNumber || item.DocumentNumber || "")?.toString();
        let customerStr = (item.customer || item.Customer || "")?.toString();
        const amountVal = item.amount ?? item.Amount ?? 0;
        const unappliedVal = item.unapplied ?? item.Unapplied;

        // Truncate customer name if long to prevent collision with Amount column
        const maxCustWidth = 220;
        if (calibriFont.widthOfTextAtSize(customerStr, 10) > maxCustWidth) {
          while (customerStr.length > 0 && calibriFont.widthOfTextAtSize(customerStr + "...", 10) > maxCustWidth) {
            customerStr = customerStr.slice(0, -1);
          }
          customerStr += "...";
        }

        // 1. Date
        currentPage.drawText(dateStr, {
          x: COL_X_DATE,
          y: yPosition,
          size: 10,
          font: calibriFont,
        });

        // 2. Doc. No.
        currentPage.drawText(docNoStr, {
          x: COL_X_DOC_NO,
          y: yPosition,
          size: 10,
          font: calibriFont,
        });

        // 3. Customer
        currentPage.drawText(customerStr, {
          x: COL_X_CUSTOMER,
          y: yPosition,
          size: 10,
          font: calibriFont,
        });

        // 4. Amount (Right-aligned)
        if (amountVal !== null && amountVal !== undefined) {
          drawTextRight(currentPage, FormatValueToDecimal(amountVal), COL_X_AMOUNT_RIGHT, yPosition, calibriFont, 10);
          cashSalesTotal += Number(amountVal) || 0;
        }

        // 5. Unapplied (Right-aligned, blank if 0 or null)
        if (unappliedVal !== null && unappliedVal !== undefined && Number(unappliedVal) !== 0) {
          drawTextRight(currentPage, FormatValueToDecimal(unappliedVal), COL_X_UNAPPLIED_RIGHT, yPosition, calibriFont, 10);
        }

        yPosition -= ROW_HEIGHT;
      }

      // --- DRAW TOTALS SECTION ---
      // Ensure enough vertical space (~80pt) for the totals block
      if (yPosition < 95) {
        const [newPage] = await pdfDoc.copyPages(templateDoc, [0]);
        pdfDoc.addPage(newPage);
        currentPage = newPage;
        drawHeader(currentPage);
        yPosition = TABLE_START_Y;
      }

      const grandTotal = cashSalesTotal + cashCollectionsTotal;

      // Top line above totals
      currentPage.drawLine({
        start: { x: TABLE_LEFT, y: yPosition + 3 },
        end: { x: TABLE_RIGHT, y: yPosition + 3 },
        thickness: 0.75,
      });
      yPosition -= 9;

      // 1. Cash Sales Total
      currentPage.drawText("Cash Sales Total", {
        x: TABLE_LEFT,
        y: yPosition,
        size: 10,
        font: calibriBold,
      });
      drawTextRight(currentPage, `P${FormatValueToDecimal(cashSalesTotal)}`, COL_X_AMOUNT_RIGHT, yPosition, calibriBold, 10);
      yPosition -= 10;

      // Line
      currentPage.drawLine({
        start: { x: TABLE_LEFT, y: yPosition },
        end: { x: TABLE_RIGHT, y: yPosition },
        thickness: 0.5,
      });
      yPosition -= 9;

      // 2. Cash Collections Total
      currentPage.drawText("Cash Collections Total", {
        x: TABLE_LEFT,
        y: yPosition,
        size: 10,
        font: calibriBold,
      });
      drawTextRight(currentPage, `P${FormatValueToDecimal(cashCollectionsTotal)}`, COL_X_AMOUNT_RIGHT, yPosition, calibriBold, 10);
      yPosition -= 10;

      // Line
      currentPage.drawLine({
        start: { x: TABLE_LEFT, y: yPosition },
        end: { x: TABLE_RIGHT, y: yPosition },
        thickness: 0.5,
      });
      yPosition -= 9;

      // 3. Grand Total
      currentPage.drawText("Grand Total", {
        x: TABLE_LEFT,
        y: yPosition,
        size: 10,
        font: calibriBold,
      });
      drawTextRight(currentPage, `P${FormatValueToDecimal(grandTotal)}`, COL_X_AMOUNT_RIGHT, yPosition, calibriBold, 10);
      yPosition -= 10;

      // Double line below Grand Total
      currentPage.drawLine({
        start: { x: TABLE_LEFT, y: yPosition },
        end: { x: TABLE_RIGHT, y: yPosition },
        thickness: 0.8,
      });
      currentPage.drawLine({
        start: { x: TABLE_LEFT, y: yPosition - 1.5 },
        end: { x: TABLE_RIGHT, y: yPosition - 1.5 },
        thickness: 0.4,
      });
      yPosition -= 12;

      // Note at the bottom
      currentPage.drawText("Note : Report includes transactions with Cash Terms came from Sales, Cash Sales, and DR Sales Module.", {
        x: TABLE_LEFT,
        y: yPosition,
        size: 10,
        font: calibriFont,
      });

      // --- DRAW PAGE NUMBERS ---
      const allPages = pdfDoc.getPages();
      for (let i = 0; i < allPages.length; i++) {
        allPages[i].drawText(`${i + 1}`, {
          x: PAGE_NO_X,
          y: 626,
          size: 10,
          font: calibriFont,
        });
      }

      // --- SAVE AND DOWNLOAD ---
      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `COLLECTION_REPORT.pdf`;
      a.click();
    } catch (error) {
      console.error("Error generating PDF:", error);
      setFailMessage("Error processing PDF");
    }

    setLoading(false);
  };
//#endregion






//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasCollectionReport) {
    return <NoAccess />;
  }
//#endregion





//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon fontSize="large" className="text-theme-color" />
              {`Collection Report`}
            </h2>
            <p className="text-gray-500 mt-1">Money that has been collected/received from customers.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-semibold mb-1 text-gray-700">
              Customer
            </label>
            <div className="flex flex-row items-center">
              <input
                type="text"
                placeholder="Select Customer"
                className="w-full border border-gray-300 text-sm rounded-l-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color transition"
                value={`${customer ? `${customer?.customerId} - ${customer?.name1}` : ""}`}
                readOnly
              />
              {!customer ?
                <button className="bg-theme-color hover:bg-theme-color-h p-[5px] rounded-r-lg" onClick={openCustomerSelect}>
                  <PersonSearchIcon className="text-white"/>
                </button> 
                :
                <button className="bg-red-400 hover:bg-red-500 p-[5px] rounded-r-lg" onClick={()=>setCustomer(null)}>
                  <RemoveCircleIcon className="text-white"/>
                </button>
              }
            </div>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Company <span className="text-red-500">*</span>
            </label>
            <CompanySelect data={company} setData={setCompany} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date From <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateFrom} setData={setDateFrom} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date To <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateTo} setData={setDateTo} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Payment Method
            </label>
            <PaymentMethodSelect data={paymentMethod} setData={setPaymentMethod} isEditable={true} />
          </div>
        </div>

        <button onClick={generateHandleClick} className="mt-10 flex items-center gap-2 bg-theme-color text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-theme-color-h transition shadow-md">
          <RestartAltIcon fontSize="small" /> Generate
        </button>
      </div>

      <CustomerSelect 
        visible={showCustomerSelect} 
        onClose={closeCustomerSelect}
        modalName={"Select Customer"}
        data={customer} setData={setCustomer}
      />

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}